import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  generateBatch,
  type Hero,
  type PfpProject,
  type Rule,
  type Trait,
} from "@/pfp/generator";
import { buildMosaicCover } from "@/pfp/cover";
import { buildBlindPlaceholder } from "@/pfp/placeholder";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { useWizard } from "@/stores/wizard";

function uid(): string {
  return crypto.randomUUID();
}

const DEFAULT_PROJECT: Omit<PfpProject, "id"> = {
  name: "Untitled collection",
  canvasSize: { width: 512, height: 512 },
  layers: [],
  rules: [],
  heroes: [],
  metadata: { namePattern: "{collection} #{id}", descriptionTemplate: "" },
};

type ServerProject = {
  id: string;
  name: string;
  projectJson: string;
  finalizedAt: number | null;
  contractAddress: string | null;
};

/**
 * Owns the PfpProject state and every mutation the editor needs:
 * layers, traits, rules, heroes, metadata. Keeps Pfp.tsx focused on
 * layout + tabs.
 *
 * Mutations are stable (`useCallback`) so they can be passed to memo'd
 * children without re-rendering them.
 */
export function usePfpProjectState() {
  const [project, setProject] = useState<PfpProject>(() => ({
    id: uid(),
    ...DEFAULT_PROJECT,
  }));

  // ---------- layers ----------
  const addLayer = useCallback(() => {
    setProject((p) => ({
      ...p,
      layers: [
        ...p.layers,
        { id: uid(), name: `Layer ${p.layers.length + 1}`, order: p.layers.length, traits: [] },
      ],
    }));
  }, []);

  const removeLayer = useCallback((layerId: string) => {
    setProject((p) => ({
      ...p,
      layers: p.layers.filter((l) => l.id !== layerId).map((l, i) => ({ ...l, order: i })),
      rules: p.rules.filter((r) => {
        if (r.kind === "incompatible") return r.a.layerId !== layerId && r.b.layerId !== layerId;
        if (r.kind === "requires") return r.if.layerId !== layerId && r.then.layerId !== layerId;
        return r.when.layerId !== layerId && r.on.layerId !== layerId;
      }),
      heroes: p.heroes.map((h) => {
        const { [layerId]: _, ...rest } = h.pinned;
        return { ...h, pinned: rest };
      }),
    }));
  }, []);

  const moveLayer = useCallback((layerId: string, dir: -1 | 1) => {
    setProject((p) => {
      const idx = p.layers.findIndex((l) => l.id === layerId);
      if (idx < 0) return p;
      const j = idx + dir;
      if (j < 0 || j >= p.layers.length) return p;
      const layers = [...p.layers];
      [layers[idx], layers[j]] = [layers[j]!, layers[idx]!];
      return { ...p, layers: layers.map((l, i) => ({ ...l, order: i })) };
    });
  }, []);

  const renameLayer = useCallback((layerId: string, name: string) => {
    setProject((p) => ({
      ...p,
      layers: p.layers.map((l) => (l.id === layerId ? { ...l, name } : l)),
    }));
  }, []);

  // ---------- traits ----------
  const addTraits = useCallback((layerId: string, files: FileList | null) => {
    if (!files) return;
    const newTraits: Trait[] = Array.from(files).map((f) => ({
      id: uid(),
      name: f.name.replace(/\.[^.]+$/u, ""),
      weight: 5,
      imageUrl: URL.createObjectURL(f),
    }));
    setProject((p) => ({
      ...p,
      layers: p.layers.map((l) =>
        l.id === layerId ? { ...l, traits: [...l.traits, ...newTraits] } : l,
      ),
    }));
  }, []);

  const updateTrait = useCallback(
    (layerId: string, traitId: string, patch: Partial<Trait>) => {
      setProject((p) => ({
        ...p,
        layers: p.layers.map((l) =>
          l.id === layerId
            ? { ...l, traits: l.traits.map((t) => (t.id === traitId ? { ...t, ...patch } : t)) }
            : l,
        ),
      }));
    },
    [],
  );

  const removeTrait = useCallback((layerId: string, traitId: string) => {
    setProject((p) => ({
      ...p,
      layers: p.layers.map((l) =>
        l.id === layerId ? { ...l, traits: l.traits.filter((t) => t.id !== traitId) } : l,
      ),
    }));
  }, []);

  // ---------- rules ----------
  /** Add a default rule of `kind`. Returns false (and surfaces a reason) if
   *  there's nothing to attach the rule to yet. */
  const addRule = useCallback((kind: Rule["kind"]): { ok: true } | { ok: false; reason: string } => {
    let result: { ok: true } | { ok: false; reason: string } = { ok: true };
    setProject((p) => {
      const layer = p.layers[0];
      const trait = layer?.traits[0];
      if (!layer || !trait) {
        result = { ok: false, reason: "Add at least one layer with a trait before defining rules." };
        return p;
      }
      const ref = { layerId: layer.id, traitId: trait.id };
      let r: Rule;
      if (kind === "incompatible") r = { kind, a: ref, b: ref };
      else if (kind === "requires") r = { kind, if: ref, then: ref };
      else r = { kind, when: ref, on: ref, multiplier: 2 };
      return { ...p, rules: [...p.rules, r] };
    });
    return result;
  }, []);

  const removeRule = useCallback((idx: number) => {
    setProject((p) => ({ ...p, rules: p.rules.filter((_, i) => i !== idx) }));
  }, []);

  const updateRule = useCallback((idx: number, patch: Partial<Rule>) => {
    setProject((p) => ({
      ...p,
      rules: p.rules.map((r, i) => (i === idx ? ({ ...r, ...patch } as Rule) : r)),
    }));
  }, []);

  // ---------- heroes ----------
  const addHero = useCallback(() => {
    setProject((p) => {
      const tokenId = (p.heroes.at(-1)?.tokenId ?? 0) + 1;
      return { ...p, heroes: [...p.heroes, { tokenId, pinned: {} } as Hero] };
    });
  }, []);

  const removeHero = useCallback((tokenId: number) => {
    setProject((p) => ({ ...p, heroes: p.heroes.filter((h) => h.tokenId !== tokenId) }));
  }, []);

  const updateHero = useCallback((tokenId: number, layerId: string, traitId: string) => {
    setProject((p) => ({
      ...p,
      heroes: p.heroes.map((h) =>
        h.tokenId === tokenId
          ? {
              ...h,
              pinned: traitId
                ? { ...h.pinned, [layerId]: traitId }
                : (() => {
                    const { [layerId]: _, ...rest } = h.pinned;
                    return rest;
                  })(),
            }
          : h,
      ),
    }));
  }, []);

  const setHeroTokenId = useCallback((oldId: number, newId: number) => {
    setProject((p) => ({
      ...p,
      heroes: p.heroes.map((h) => (h.tokenId === oldId ? { ...h, tokenId: newId } : h)),
    }));
  }, []);

  return {
    project,
    setProject,
    layers: { add: addLayer, remove: removeLayer, move: moveLayer, rename: renameLayer },
    traits: { add: addTraits, update: updateTrait, remove: removeTrait },
    rules: { add: addRule, remove: removeRule, update: updateRule },
    heroes: { add: addHero, remove: removeHero, update: updateHero, setTokenId: setHeroTokenId },
  };
}

/**
 * Owns the persist + deploy lifecycle: save to /api/pfp, then on
 * Pin&Deploy run generateBatch, pin everything, finalize on the worker,
 * pre-fill the wizard and bounce to /create.
 */
export function usePfpDeploy({
  project,
  setProject,
  count,
  serverId,
  setServerId,
}: {
  project: PfpProject;
  setProject: React.Dispatch<React.SetStateAction<PfpProject>>;
  count: number;
  serverId: string | null;
  setServerId: (id: string) => void;
}) {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const wizard = useWizard();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const saveProject = useCallback(async () => {
    if (!isAuthed) {
      setStatus("Sign in to save your project.");
      return;
    }
    setStatus("Saving…");
    try {
      const body = { name: project.name, project };
      if (serverId) {
        await api.patch(`/api/pfp/${serverId}`, body, { auth: true });
      } else {
        const resp = await api.post<{ project: ServerProject }>("/api/pfp", body, { auth: true });
        setServerId(resp.project.id);
        setProject((p) => ({ ...p, id: resp.project.id }));
      }
      setStatus("Saved.");
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  }, [isAuthed, project, serverId, setProject, setServerId]);

  const pinAndDeploy = useCallback(async () => {
    if (!isAuthed) {
      setStatus("Sign in to deploy.");
      return;
    }
    if (project.layers.length === 0 || project.layers.some((l) => l.traits.length === 0)) {
      setStatus("Every layer needs at least one trait.");
      return;
    }
    if (count <= 0) return;
    setBusy(true);
    setStatus("Generating tokens…");
    setProgress({ done: 0, total: count });
    try {
      let pid = serverId;
      if (!pid) {
        const resp = await api.post<{ project: ServerProject }>(
          "/api/pfp",
          { name: project.name, project },
          { auth: true },
        );
        pid = resp.project.id;
        setServerId(pid);
      } else {
        await api.patch(`/api/pfp/${pid}`, { name: project.name, project }, { auth: true });
      }

      const results = await generateBatch(project, {
        count,
        onProgress: (done, total) => setProgress({ done, total }),
      });

      setStatus("Building collection cover mosaic…");
      // Reusing tokens[0]'s image as the cover (the previous default)
      // made every PFP collection look like its first token. Build a
      // square-grid mosaic from up to 16 of the rendered tokens
      // instead, so the home tile stays visually distinct.
      const coverBlob = await buildMosaicCover(
        results.map((r) => r.blob),
        project.canvasSize,
      );
      const coverFile = new File([coverBlob], "cover.png", { type: "image/png" });
      const { cid: coverCid } = await api.uploadFile(coverFile);

      setStatus("Generating blind-mint placeholder…");
      // Blind-by-default for PFPs: the contract starts with empty baseURI
      // and serves this placeholder for every tokenURI() until the
      // creator hits "Reveal" in the dashboard. Image is rendered
      // procedurally — no extra OpenAI / artist round-trip.
      const placeholderBlob = await buildBlindPlaceholder(project.canvasSize, project.name);
      const placeholderFile = new File([placeholderBlob], "placeholder.png", { type: "image/png" });
      const { cid: placeholderImageCid } = await api.uploadFile(placeholderFile);
      const { cid: placeholderJsonCid } = await api.uploadJson({
        name: `${project.name} — pre-reveal`,
        description:
          "Blind mint: every token shows this placeholder until the creator " +
          "calls reveal(). Your final art arrives at reveal.",
        image: `ipfs://${placeholderImageCid}`,
      });

      setStatus("Pinning images and metadata to IPFS…");
      const tokens: {
        tokenId: number;
        imageCid: string;
        metadataCid: string;
        attributes: { trait_type: string; value: string }[];
      }[] = [];
      for (let i = 0; i < results.length; i++) {
        const r = results[i]!;
        const file = new File([r.blob], `${r.tokenId}.png`, { type: "image/png" });
        const { cid: imageCid } = await api.uploadFile(file);
        const finalMeta = { ...r.metadata, image: `ipfs://${imageCid}` };
        const { cid: metadataCid } = await api.uploadJson(finalMeta);
        tokens.push({ tokenId: r.tokenId, imageCid, metadataCid, attributes: r.attributes });
        setStatus(`Pinned ${i + 1} / ${results.length}…`);
      }

      const fin = await api.post<{ baseUri: string }>(
        `/api/pfp/${pid}/finalize`,
        { tokens },
        { auth: true },
      );

      // The real baseURI lives off-chain only until reveal: stash it in
      // localStorage keyed by projectId so the dashboard's reveal button
      // can recover it. (For multi-device support the API's pfp_projects
      // row is still the source of truth — `fin.baseUri` is just
      // ${apiUrl}/metadata/projects/${pid}/, derivable from the project.)
      try {
        if (pid) localStorage.setItem(`atelier.blindReveal.${pid}`, fin.baseUri);
      } catch {
        // localStorage might be unavailable (private mode, etc.); the
        // dashboard falls back to deriving the URL from the project id.
      }

      wizard.applyPreset({
        kind: "drop",
        name: project.name,
        symbol: (project.name.replace(/[^A-Za-z]/g, "").slice(0, 5) || "PFP").toUpperCase(),
        description: project.metadata.descriptionTemplate ?? "",
        imageCid: coverCid,
        // baseUri is intentionally empty — blindMint=true forces the
        // contract to keep the real metadata pointer off-chain until
        // reveal(). The wizard preset still carries the real one
        // through fin.baseUri (above) for the reveal flow.
        baseUri: "",
        maxSupply: tokens.length,
        mintPriceEth: "0.001",
        pfpProjectId: pid ?? undefined,
        revealEnabled: true,
        placeholderCid: placeholderJsonCid,
        blindMint: true,
        step: 2,
      });
      setStatus("Generated and pinned. Redirecting to deploy…");
      setTimeout(() => navigate("/create"), 700);
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [count, isAuthed, navigate, project, serverId, setServerId, wizard]);

  return { saveProject, pinAndDeploy, busy, progress, status, setStatus };
}
